import XLSX from 'xlsx-js-style';

// ── Brand colors ──────────────────────────────────────────────────────────
const BRAND = {
  dark:    { rgb: '064E3B' },   // emerald-900
  medium:  { rgb: '059669' },   // emerald-600
  light:   { rgb: 'D1FAE5' },   // emerald-100
  vLight:  { rgb: 'ECFDF5' },   // emerald-50
  white:   { rgb: 'FFFFFF' },
  grayBg:  { rgb: 'F8FAFC' },   // slate-50
  grayBd:  { rgb: 'E2E8F0' },   // slate-200
  red:     { rgb: 'DC2626' },
  amber:   { rgb: 'D97706' },
  text:    { rgb: '1E293B' },   // slate-800
  subtext: { rgb: '64748B' },   // slate-500
};

const FONT_MAIN = { name: 'Calibri', sz: 11, color: { rgb: '1E293B' } };
const FONT_HEADER = { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFF' } };
const FONT_SECTION = { name: 'Calibri', sz: 13, bold: true, color: { rgb: '064E3B' } };
const FONT_TITLE = { name: 'Calibri', sz: 18, bold: true, color: { rgb: '064E3B' } };
const FONT_SUBTITLE = { name: 'Calibri', sz: 11, color: { rgb: '64748B' } };
const FONT_BOLD = { name: 'Calibri', sz: 11, bold: true, color: { rgb: '1E293B' } };
const FONT_MONEY_GREEN = { name: 'Calibri', sz: 11, bold: true, color: { rgb: '059669' } };
const FONT_MONEY_RED = { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'DC2626' } };

const BORDER_THIN = {
  top:    { style: 'thin', color: { rgb: 'E2E8F0' } },
  bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
  left:   { style: 'thin', color: { rgb: 'E2E8F0' } },
  right:  { style: 'thin', color: { rgb: 'E2E8F0' } },
};

const BORDER_BOTTOM_THICK = {
  ...BORDER_THIN,
  bottom: { style: 'medium', color: { rgb: '064E3B' } },
};

const NUM_FMT_DOLLAR = '"$"#,##0';
const NUM_FMT_PCT = '0.0%';
const NUM_FMT_PCT2 = '0.00%';

// ── Style helpers ─────────────────────────────────────────────────────────

function headerCell(v) {
  return {
    v,
    t: 's',
    s: {
      font: FONT_HEADER,
      fill: { fgColor: BRAND.dark },
      border: BORDER_THIN,
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    },
  };
}

function headerCellLeft(v) {
  return { ...headerCell(v), s: { ...headerCell(v).s, alignment: { horizontal: 'left', vertical: 'center', wrapText: true } } };
}

function labelCell(v, indent = false) {
  return {
    v,
    t: 's',
    s: {
      font: indent ? { ...FONT_MAIN, color: BRAND.subtext } : FONT_MAIN,
      border: BORDER_THIN,
      alignment: { horizontal: 'left', vertical: 'center', indent: indent ? 1 : 0 },
    },
  };
}

function moneyCell(v, opts = {}) {
  const font = opts.green ? FONT_MONEY_GREEN : opts.red ? FONT_MONEY_RED : opts.bold ? FONT_BOLD : FONT_MAIN;
  return {
    v,
    t: 'n',
    s: {
      font,
      numFmt: NUM_FMT_DOLLAR,
      border: BORDER_THIN,
      alignment: { horizontal: 'right', vertical: 'center' },
      fill: opts.fill ? { fgColor: opts.fill } : undefined,
    },
  };
}

function pctCell(v, opts = {}) {
  return {
    v,
    t: 'n',
    s: {
      font: opts.bold ? FONT_BOLD : FONT_MAIN,
      numFmt: opts.precise ? NUM_FMT_PCT2 : NUM_FMT_PCT,
      border: BORDER_THIN,
      alignment: { horizontal: 'right', vertical: 'center' },
    },
  };
}

function textCell(v, opts = {}) {
  return {
    v,
    t: 's',
    s: {
      font: opts.bold ? FONT_BOLD : opts.subtitle ? FONT_SUBTITLE : FONT_MAIN,
      border: opts.noBorder ? undefined : BORDER_THIN,
      alignment: { horizontal: opts.align || 'left', vertical: 'center', wrapText: opts.wrap },
      fill: opts.fill ? { fgColor: opts.fill } : undefined,
    },
  };
}

function blankCell(fill) {
  return { v: '', t: 's', s: { border: BORDER_THIN, fill: fill ? { fgColor: fill } : undefined } };
}

function sectionTitle(v) {
  return { v, t: 's', s: { font: FONT_SECTION, border: BORDER_BOTTOM_THICK, alignment: { vertical: 'center' } } };
}

function titleCell(v) {
  return { v, t: 's', s: { font: FONT_TITLE, alignment: { vertical: 'center' } } };
}

function subtitleCell(v) {
  return { v, t: 's', s: { font: FONT_SUBTITLE, alignment: { vertical: 'center', wrapText: true } } };
}

// Footer row styling
function footerLabel(v) {
  return {
    v, t: 's',
    s: { font: { ...FONT_BOLD, color: { rgb: '064E3B' } }, fill: { fgColor: BRAND.light }, border: BORDER_THIN, alignment: { horizontal: 'left', vertical: 'center' } },
  };
}

function footerMoney(v, opts = {}) {
  return {
    v, t: 'n',
    s: { font: { ...FONT_BOLD, color: { rgb: '064E3B' } }, numFmt: NUM_FMT_DOLLAR, fill: { fgColor: BRAND.light }, border: BORDER_THIN, alignment: { horizontal: 'right', vertical: 'center' } },
  };
}

function footerText(v) {
  return {
    v, t: 's',
    s: { font: { ...FONT_BOLD, color: { rgb: '064E3B' } }, fill: { fgColor: BRAND.light }, border: BORDER_THIN, alignment: { horizontal: 'right', vertical: 'center' } },
  };
}

function footerBlank() {
  return { v: '', t: 's', s: { fill: { fgColor: BRAND.light }, border: BORDER_THIN } };
}

// Alt-row fill
function altFill(i) {
  return i % 2 === 1 ? BRAND.grayBg : undefined;
}

// ── Sheet builders ────────────────────────────────────────────────────────

function buildCoverSheet(inputs, calc) {
  const rows = [];
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  rows.push([titleCell('NMTC Deal Model — Summary Report')]);
  rows.push([subtitleCell('Prepared by Clarity Impact Finance')]);
  rows.push([subtitleCell(today)]);
  rows.push([]);
  rows.push([subtitleCell('This report was generated from the interactive NMTC Deal Model at clarityimpactfinance.com/nmtc-model.')]);
  rows.push([subtitleCell('For illustrative purposes only. Consult a qualified advisor before structuring a transaction.')]);
  rows.push([]);
  rows.push([]);

  // Deal summary section
  rows.push([sectionTitle('Deal Summary'), blankCell(), blankCell()]);
  rows.push([]);

  const kv = [
    ['Qualified Equity Investment (QEI)', calc.qei, true],
    ['Owner Equity', calc.ownerEquity],
    ['Hard Debt (Leverage Loan)', calc.hardDebt],
    ['NMTC Equity (Tax Credit Proceeds)', calc.nmtcEquity],
    ['Total Leverage Loan', calc.leverageLoan],
    [],
    ['Tax Credit Price', inputs.taxCreditPrice, false, true],
    ['Hard Debt Rate', inputs.hardDebtRate / 100, false, false, true],
    ['Loan Term', `${inputs.loanTerm} years`],
    ['Sub-Allocation Fee', inputs.subAllocationPct / 100, false, false, true],
    [],
    ['A-Note (Senior)', calc.aNote],
    ['B-Note (Subordinate)', calc.bNote],
    ['A-Note Rate', calc.aNoteRate, false, false, true],
    ['B-Note Rate', calc.bNoteRate, false, false, true],
    [],
    ['Effective Rate (NMTC)', calc.effectiveRate, false, false, true],
    ['Net Subsidy (NPV)', calc.netSubsidyNPV, false, false, false, true],
    ['Net Subsidy (% of QEI)', calc.subsidyPct, false, false, true],
    ['Total 7-Year Benefit', calc.nmtcSavings7Year, true],
  ];

  kv.forEach(row => {
    if (row.length === 0) { rows.push([]); return; }
    const [label, val, highlight, isTcPrice, isPct, isGreen] = row;
    if (typeof val === 'string') {
      rows.push([labelCell(label), textCell(val, { bold: true, align: 'right' })]);
    } else if (isPct) {
      rows.push([labelCell(label), pctCell(val, { bold: highlight, precise: true })]);
    } else if (isTcPrice) {
      rows.push([labelCell(label), { v: val, t: 'n', s: { font: FONT_BOLD, numFmt: '"$"0.00', border: BORDER_THIN, alignment: { horizontal: 'right' } } }]);
    } else {
      rows.push([labelCell(label), moneyCell(val, { bold: highlight, green: isGreen })]);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 38 }, { wch: 22 }];
  // Merge title across columns
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 1 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: 1 } },
  ];
  return ws;
}

function buildSourcesUsesSheet(calc, inputs) {
  const rows = [];

  rows.push([sectionTitle('Sources & Uses of Funds'), blankCell(), blankCell()]);
  rows.push([]);

  // Sources
  rows.push([headerCellLeft('Sources'), headerCell('Amount'), headerCell('% of QEI')]);
  const sources = [
    ['Owner Equity', calc.ownerEquity, calc.ownerEquityPct / 100],
    ['Hard Debt', calc.hardDebt, calc.hardDebtPct / 100],
    ['NMTC Equity (Tax Credits)', calc.nmtcEquity, calc.equityPct / 100],
  ];
  sources.forEach(([l, v, p], i) => {
    rows.push([
      { ...labelCell(l), s: { ...labelCell(l).s, fill: altFill(i) ? { fgColor: altFill(i) } : undefined } },
      { ...moneyCell(v), s: { ...moneyCell(v).s, fill: altFill(i) ? { fgColor: altFill(i) } : undefined } },
      { ...pctCell(p), s: { ...pctCell(p).s, fill: altFill(i) ? { fgColor: altFill(i) } : undefined } },
    ]);
  });
  rows.push([footerLabel('Total QEI'), footerMoney(calc.qei), footerText('100.0%')]);

  rows.push([]);
  rows.push([]);

  // Uses (fees)
  rows.push([headerCellLeft('Transaction Costs'), headerCell('Amount'), headerCell('% of QEI')]);
  const fees = [
    ['Sub-Allocation Fee', calc.subAllocationFee],
    ['NMTC Consultant Fee', calc.nmtcConsultantFee],
    ['Attorney Fees', calc.attorneyFees],
    ['Accountant Fees', calc.accountantFees],
    ['Title Insurance', calc.titleInsurance],
    ['Performance Bond', calc.performanceBondFees],
  ];
  fees.forEach(([l, v], i) => {
    const f = altFill(i);
    rows.push([
      { ...labelCell(l), s: { ...labelCell(l).s, fill: f ? { fgColor: f } : undefined } },
      { ...moneyCell(v, { red: true }), s: { ...moneyCell(v, { red: true }).s, fill: f ? { fgColor: f } : undefined } },
      { ...pctCell(v / calc.qei), s: { ...pctCell(v / calc.qei).s, fill: f ? { fgColor: f } : undefined } },
    ]);
  });
  rows.push([footerLabel('Total Closing Costs'), footerMoney(calc.totalClosingFees), footerText('')]);
  rows.push([]);
  rows.push([labelCell('Net Proceeds to Project'), moneyCell(calc.netProceedsToProject, { green: true }), pctCell(calc.netProceedsToProject / calc.qei)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 30 }, { wch: 18 }, { wch: 14 }];
  return ws;
}

function buildNetBenefitSheet(calc, inputs) {
  const rows = [];
  const annualNMTC = calc.totalAnnualNMTCService + calc.assetMgmtFeeAnnual;
  const annualSavings = calc.convFullQEIAnnual - annualNMTC;
  const totalFees = calc.subAllocationFee + calc.nmtcConsultantFee + calc.attorneyFees + calc.accountantFees + calc.titleInsurance + calc.performanceBondFees + calc.assetMgmtFeeTotal;
  const net = calc.bNote - totalFees;

  rows.push([sectionTitle('Net Benefit Analysis'), blankCell(), blankCell(), blankCell()]);
  rows.push([]);

  // With vs Without comparison
  rows.push([headerCellLeft(''), headerCell('Without NMTC'), headerCell('With NMTC'), headerCell('Savings')]);
  const comp = [
    ['Total Financing', calc.qei, calc.qei, null],
    ['Annual Payment', calc.convFullQEIAnnual, annualNMTC, annualSavings],
    ['Monthly Payment', calc.convFullQEIAnnual / 12, annualNMTC / 12, annualSavings / 12],
    ['NMTC Closing Fees', 0, calc.totalClosingFees, -calc.totalClosingFees],
    ['7-Year Total Cost', calc.convFullQEI7Years, calc.nmtcTotal7YearCost, calc.convFullQEI7Years - calc.nmtcTotal7YearCost],
    ['Debt Forgiven at Year 7', 0, calc.bNote, calc.bNote],
    ['Balance Remaining at Year 7', null, calc.aNote, null],
    ['Effective Rate', inputs.hardDebtRate / 100, calc.effectiveRate, null],
  ];

  comp.forEach(([l, without, withNmtc, savings], i) => {
    const f = altFill(i);
    const fillS = f ? { fgColor: f } : undefined;
    const isRate = l === 'Effective Rate';

    rows.push([
      { ...labelCell(l, false), s: { ...labelCell(l).s, font: l.includes('7-Year') ? FONT_BOLD : FONT_MAIN, fill: fillS } },
      without === null
        ? { ...textCell('still amortizing', { align: 'right' }), s: { ...textCell('').s, font: { ...FONT_MAIN, italic: true, color: BRAND.subtext }, fill: fillS } }
        : isRate ? { ...pctCell(without, { precise: true }), s: { ...pctCell(without).s, fill: fillS } }
        : { ...moneyCell(without, { bold: l.includes('7-Year') }), s: { ...moneyCell(without).s, fill: fillS } },
      isRate ? { ...pctCell(withNmtc, { bold: true, precise: true }), s: { ...pctCell(withNmtc).s, font: FONT_MONEY_GREEN, fill: fillS } }
        : { ...moneyCell(withNmtc, { green: l.includes('Forgiven'), bold: l.includes('7-Year') }), s: { ...moneyCell(withNmtc).s, fill: fillS } },
      savings === null
        ? { ...blankCell(f), s: { ...blankCell().s, fill: fillS } }
        : savings < 0
          ? { ...moneyCell(savings, { red: true }), s: { ...moneyCell(savings).s, fill: fillS } }
          : { ...moneyCell(savings, { green: true }), s: { ...moneyCell(savings).s, fill: fillS } },
    ]);
  });

  rows.push([
    footerLabel('Total 7-Year Benefit'),
    footerBlank(),
    footerBlank(),
    footerMoney(calc.nmtcSavings7Year),
  ]);

  rows.push([]);
  rows.push([]);

  // Subsidy waterfall
  rows.push([sectionTitle('Subsidy Waterfall'), blankCell()]);
  rows.push([]);
  rows.push([labelCell('B-Note Forgiveness (Gross Benefit)'), moneyCell(calc.bNote, { green: true })]);
  rows.push([]);
  rows.push([textCell('Less: Transaction Costs', { bold: true }), blankCell()]);

  const feeItems = [
    ['Sub-Allocation Fee', calc.subAllocationFee],
    ['NMTC Consultant Fee', calc.nmtcConsultantFee],
    ['Attorney Fees', calc.attorneyFees],
    ['Accountant Fees', calc.accountantFees],
    ['Title Insurance', calc.titleInsurance],
    ['Performance Bond', calc.performanceBondFees],
    ['Asset Management (7 years)', calc.assetMgmtFeeTotal],
  ];
  feeItems.forEach(([l, v]) => {
    rows.push([labelCell('  ' + l, true), moneyCell(-v, { red: true })]);
  });

  rows.push([footerLabel('Total Fees'), footerMoney(-totalFees)]);
  rows.push([]);
  rows.push([
    { ...labelCell('Net Subsidy'), s: { font: { ...FONT_BOLD, sz: 13, color: { rgb: '064E3B' } }, border: BORDER_BOTTOM_THICK } },
    { ...moneyCell(net, { green: true }), s: { font: { ...FONT_MONEY_GREEN, sz: 13 }, numFmt: NUM_FMT_DOLLAR, border: BORDER_BOTTOM_THICK, alignment: { horizontal: 'right' } } },
  ]);
  rows.push([labelCell('As % of QEI'), pctCell(calc.qei > 0 ? net / calc.qei : 0)]);
  rows.push([labelCell('As % of Gross'), pctCell(calc.bNote > 0 ? net / calc.bNote : 0)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 34 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
  return ws;
}

function buildCreditScheduleSheet(calc) {
  const rows = [];
  rows.push([sectionTitle('7-Year Tax Credit Schedule'), blankCell(), blankCell(), blankCell()]);
  rows.push([]);
  rows.push([headerCell('Year'), headerCell('Credit Rate'), headerCell('Annual Credits'), headerCell('Cumulative')]);

  calc.creditSchedule.forEach((row, i) => {
    const f = altFill(i);
    const fillS = f ? { fgColor: f } : undefined;
    rows.push([
      { ...textCell(String(row.year), { align: 'center' }), s: { ...textCell('').s, alignment: { horizontal: 'center' }, fill: fillS } },
      { ...textCell(row.rate, { align: 'center' }), s: { ...textCell('').s, alignment: { horizontal: 'center' }, fill: fillS } },
      { ...moneyCell(row.credits), s: { ...moneyCell(row.credits).s, fill: fillS } },
      { ...moneyCell(row.cumulative), s: { ...moneyCell(row.cumulative).s, fill: fillS } },
    ]);
  });

  rows.push([footerLabel('Total'), footerText('39%'), footerMoney(calc.totalTaxCredits), footerMoney(calc.totalTaxCredits)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 18 }];
  return ws;
}

function buildDebtServiceSheet(calc, inputs) {
  const rows = [];
  rows.push([sectionTitle('7-Year Debt Service Schedule'), blankCell(), blankCell(), blankCell(), blankCell(), blankCell(), blankCell()]);
  rows.push([subtitleCell(`NMTC: interest-only.  Conventional: ${inputs.loanTerm}-year amortizing at ${inputs.hardDebtRate.toFixed(2)}%.`)]);
  rows.push([]);

  rows.push([
    headerCell('Year'),
    headerCell('A-Note (I/O)'),
    headerCell('B-Note (I/O)'),
    headerCell('NMTC Subtotal'),
    headerCell('Asset Mgmt'),
    headerCell('Total Annual'),
    { ...headerCell('Conventional'), s: { ...headerCell('').s, fill: { fgColor: { rgb: '475569' } } } },
  ]);

  calc.debtServiceSchedule.forEach((row, i) => {
    const f = altFill(i);
    const isY7 = row.year === 7;
    const fillS = isY7 ? { fgColor: { rgb: 'FEF9C3' } } : f ? { fgColor: f } : undefined;

    rows.push([
      { ...textCell(String(row.year), { align: 'center', bold: isY7 }), s: { ...textCell('').s, alignment: { horizontal: 'center' }, font: isY7 ? FONT_BOLD : FONT_MAIN, fill: fillS } },
      { ...moneyCell(row.aNotePayment), s: { ...moneyCell(0).s, fill: fillS } },
      { ...moneyCell(row.bNotePayment), s: { ...moneyCell(0).s, fill: fillS } },
      { ...moneyCell(row.totalNMTCPayment, { green: true }), s: { ...moneyCell(0, { green: true }).s, fill: fillS } },
      { ...moneyCell(row.assetMgmtFee), s: { ...moneyCell(0).s, font: { ...FONT_MAIN, color: BRAND.amber }, fill: fillS } },
      { ...moneyCell(row.totalAnnualCost, { bold: true }), s: { ...moneyCell(0, { bold: true }).s, fill: fillS } },
      { ...moneyCell(row.hardDebtAmortizing), s: { ...moneyCell(0).s, font: { ...FONT_MAIN, color: BRAND.subtext }, fill: fillS } },
    ]);
  });

  // B-Note forgiveness row
  rows.push([
    { v: `Year 7 — B-Note Forgiven: $${Math.round(calc.bNote).toLocaleString()}`, t: 's',
      s: { font: { ...FONT_BOLD, color: { rgb: '059669' } }, fill: { fgColor: BRAND.vLight }, border: BORDER_THIN, alignment: { horizontal: 'left' } } },
    ...Array(6).fill({ v: '', t: 's', s: { fill: { fgColor: BRAND.vLight }, border: BORDER_THIN } }),
  ]);

  // Totals
  rows.push([
    footerLabel('7-Year Total'),
    footerMoney(calc.annualANote * 7),
    footerMoney(calc.annualBNote * 7),
    footerMoney(calc.totalNMTCService7Years),
    footerMoney(calc.assetMgmtFeeTotal),
    footerMoney(calc.totalNMTCService7Years + calc.assetMgmtFeeTotal),
    footerMoney(calc.hardDebtAmortizingAnnual * 7),
  ]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 16 }];
  // Merge the B-Note forgiveness row
  const forgivenessRow = 3 + 1 + 7; // section + subtitle + blank + header + 7 data rows
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    { s: { r: forgivenessRow, c: 0 }, e: { r: forgivenessRow, c: 6 } },
  ];
  return ws;
}

function buildUnwindSheet(calc, inputs) {
  const rows = [];
  rows.push([sectionTitle('Unwind at Year 7'), blankCell()]);
  rows.push([]);

  const items = [
    ['B-Note Forgiven', calc.bNote, true],
    ['A-Note Remaining (to refinance)', calc.aNote, false],
    ['Estimated Unwind Legal Costs', calc.unwindCostEstimate, false, true],
    [],
    ['Cancellation of Debt Income', calc.codIncomeEstimate, false],
    ['Estimated Tax on COD (@ 30%)', calc.codTaxEstimate, false, true],
  ];

  items.forEach(row => {
    if (row.length === 0) { rows.push([]); return; }
    const [l, v, green, red] = row;
    rows.push([labelCell(l), moneyCell(v, { green, red })]);
  });

  rows.push([]);
  rows.push([]);
  rows.push([textCell('Note: For-profit entities will recognize COD income when the B-Note is forgiven.', { subtitle: true, wrap: true })]);
  rows.push([textCell('Non-profit organizations are generally exempt from COD income recognition.', { subtitle: true, wrap: true })]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 38 }, { wch: 22 }];
  return ws;
}

// ── Main export function ──────────────────────────────────────────────────

export default function exportDealExcel(inputs, calc) {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildCoverSheet(inputs, calc), 'Deal Summary');
  XLSX.utils.book_append_sheet(wb, buildSourcesUsesSheet(calc, inputs), 'Sources & Uses');
  XLSX.utils.book_append_sheet(wb, buildNetBenefitSheet(calc, inputs), 'Net Benefit');
  XLSX.utils.book_append_sheet(wb, buildCreditScheduleSheet(calc), 'Tax Credit Schedule');
  XLSX.utils.book_append_sheet(wb, buildDebtServiceSheet(calc, inputs), 'Debt Service');
  XLSX.utils.book_append_sheet(wb, buildUnwindSheet(calc, inputs), 'Unwind');

  const qeiK = Math.round(calc.qei / 1000);
  const filename = `NMTC_Deal_Analysis_${qeiK}K_QEI.xlsx`;
  XLSX.writeFile(wb, filename);

  // Track download in GA4
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'file_download', {
      file_name: filename,
      file_extension: 'xlsx',
      content_type: 'nmtc_deal_analysis',
    });
  }
}
